/**
 * Banner que detecta cuando hay una versión nueva del PMIS desplegada
 * y ofrece al usuario recargar para activarla. También muestra un
 * mensaje cuando la app queda lista para uso offline por primera vez.
 *
 * Funciona con la registración del service worker que `vite-plugin-pwa`
 * inyecta automáticamente en build. En `npm run dev` el SW está
 * deshabilitado (devOptions.enabled=false), así que este banner no
 * aparece durante desarrollo.
 */

import { Button, MessageBar, MessageBarBody, MessageBarTitle, MessageBarActions } from "@fluentui/react-components";
// `virtual:pwa-register/react` es un módulo virtual que vite-plugin-pwa
// expone solo cuando el plugin está activo (production build).
// El tipo viene del paquete `vite-plugin-pwa/client.d.ts` referenciado
// en `src/vite-env.d.ts`.
import { useRegisterSW } from "virtual:pwa-register/react";

export function PWAUpdatePrompt() {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      if (import.meta.env.DEV) {
        console.info("[PWA] Service Worker registrado:", swUrl);
      }
      if (!r) return;
      // Auto-detección de versiones nuevas SIN que el usuario tenga que
      // reabrir la app: revisamos el servidor periódicamente y cada vez que
      // la pestaña vuelve a primer plano. Si hay un `dist` nuevo desplegado,
      // dispara `needRefresh` → aparece el banner "Nueva versión disponible".
      const revisar = () => { void r.update().catch(() => {}); };
      setInterval(revisar, 60 * 60 * 1000); // cada hora
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") revisar();
      });
    },
    onRegisterError(error) {
      console.error("[PWA] Error registrando SW:", error);
    },
  });

  const cerrar = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  // Caso 1: hay una versión nueva esperando — pedir al usuario recargar.
  if (needRefresh) {
    return (
      <div style={contenedorEstilo}>
        <MessageBar intent="info" politeness="assertive">
          <MessageBarBody>
            <MessageBarTitle>Nueva versión disponible</MessageBarTitle>
            Hay una actualización del PMIS lista. Recarga para usarla — tus datos no se pierden.
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="primary" size="small" onClick={() => updateServiceWorker(true)}>
              Recargar ahora
            </Button>
            <Button appearance="subtle" size="small" onClick={cerrar}>
              Después
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  // Caso 2: primera instalación / cache lista — informativo.
  if (offlineReady) {
    return (
      <div style={contenedorEstilo}>
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>PMIS listo para uso offline</MessageBarTitle>
            La app quedó cacheada en este dispositivo. Puedes seguir usándola si te quedas sin red.
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="subtle" size="small" onClick={cerrar}>
              Entendido
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  return null;
}

const contenedorEstilo: React.CSSProperties = {
  position: "fixed",
  bottom: 16,
  right: 16,
  zIndex: 9999,
  maxWidth: 420,
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  borderRadius: 6,
};
